import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useId } from 'react'
import { useMatch, useNavigate } from 'react-router'
import {
  type NotificationPreferencesViewModel,
  type PreferenceItemViewModel,
} from '@/features/activity/NotificationPreferencesViewModel'
import { useActivation } from '@/hooks/view-model-hooks'
import { links } from '@/utils/link'
import { ignoreAbortError } from '@/utils/abort-util'
import { useObservable } from '@/hooks/rx-hooks'
import {
  NotificationPreferencesEditorDialogView,
  PreferenceRow,
  PreferenceRowSkeleton,
  PreferencesTable,
} from './index'
import { SkeletonList } from '@/components/Skeleton'

export interface NotificationPreferencesEditorDialogViewContainerProps {
  vm: NotificationPreferencesViewModel
}

export const NotificationPreferencesEditorDialogViewContainer = observer(
  function NotificationPreferencesEditorDialogViewContainer({
    vm,
  }: NotificationPreferencesEditorDialogViewContainerProps) {
    const navigate = useNavigate()
    const formId = useId()
    const showingPreferences = !!useMatch(links.activityPreferences())

    const close = useCallback(() => {
      void navigate(
        {
          pathname: links.activity(),
        },
        { replace: true },
      )
    }, [navigate])

    useActivation(vm)
    useObservable(vm.requestClose$, close)

    useEffect(() => {
      if (!showingPreferences) {
        return
      }

      const controller = new AbortController()
      void vm.fetchPreferences(controller.signal).catch(ignoreAbortError())
      return () => {
        controller.abort()
      }
    }, [vm, showingPreferences])

    return (
      <NotificationPreferencesEditorDialogView
        isOpen={showingPreferences}
        isPending={vm.updatePreferences.pending}
        formId={formId}
        onClose={close}
        onSubmit={vm.updatePreferences}
      >
        <PreferencesTableContainer
          vm={vm}
          isDisabled={vm.updatePreferences.pending}
        />
      </NotificationPreferencesEditorDialogView>
    )
  },
)

interface PreferencesTableContainerProps {
  vm: NotificationPreferencesViewModel
  isDisabled: boolean
}

export const PreferencesTableContainer = observer(
  function PreferencesTableContainer({
    vm,
    isDisabled,
  }: PreferencesTableContainerProps) {
    return (
      <PreferencesTable>
        {vm.fetchPreferences.pending ? (
          <SkeletonList count={8}>
            <PreferenceRowSkeleton />
          </SkeletonList>
        ) : (
          <>
            <PreferenceRowContainer
              isDisabled={isDisabled}
              item={vm.globalPreferences}
            />
            {Array.from(vm.typeSpecificPreferences.entries()).map(
              ([type, item]) => (
                <PreferenceRowContainer
                  isDisabled={isDisabled}
                  item={item}
                  key={type}
                />
              ),
            )}
          </>
        )}
      </PreferencesTable>
    )
  },
)

interface PreferenceRowContainerProps {
  item: PreferenceItemViewModel
  isDisabled?: boolean
}

const PreferenceRowContainer = observer(function PreferenceRowContainer({
  item,
  isDisabled,
}: PreferenceRowContainerProps) {
  return (
    <PreferenceRow
      title={item.title}
      description={item.description || undefined}
      emailEnabled={item.emailEnabled}
      pushEnabled={item.pushEnabled}
      isEmailDisabled={item.isEmailOverridden}
      isPushDisabled={item.isPushOverridden}
      isDisabled={isDisabled}
      onEmailChange={item.setEmailEnabled}
      onPushChange={item.setPushEnabled}
    />
  )
})
